import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FileText, User, Building, Calendar, Package, DollarSign, Calculator } from 'lucide-react';
import { getPurchaseOrderDetails, updatePurchaseOrder } from '../../../models/requisitionModel';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';

const ViewPurchaseOrder = () => {
    const { requisitionId } = useParams();
    const navigate = useNavigate();
    const { user: authUser, can, isAdmin } = useAuth();
    
    // State management
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    
    // Form state for PO updates
    const [formData, setFormData] = useState({
        quotation_items: [],
        quotation_qty: [],
        quotation_units: [],
        quotation_discounts: [],
        quotation_amounts: [],
        quotation_vats: [],
        quotation_before_discount: 0,
        quotation_discount: 0,
        quotation_subtotal: 0,
        quotation_vat: 0,
        quotation_total: 0,
        discount_type: 'total',
        vat_type: 'total'
    });
    
    // Calculation refs
    const vatRate = 15; // 15% VAT
    const originalTotal = useRef(0);

    // Load PO data
    const loadPOData = async () => {
        setLoading(true);
        try {
            const response = await getPurchaseOrderDetails(requisitionId);
            
            if (response.success) {
                setData(response.data);
                originalTotal.current = parseFloat(response.data.selected?.quotation_total || 0);
                
                // Initialize form data from requisition data
                initializeFormData(response.data);
                
                toast.success('Purchase order loaded successfully');
            } else {
                throw new Error(response.message || 'Failed to fetch purchase order details');
            }
        } catch (error) {
            console.error('Error loading PO data:', error);
            toast.error('Failed to load purchase order: ' + error.message);
            navigate('/requisitions/po');
        } finally {
            setLoading(false);
        }
    };

    // Initialize form data from requisition data
    const initializeFormData = (requisitionData) => {
        const items = deserializeArray(requisitionData.requisition_items || requisitionData.procurement_items);
        const qty = deserializeArray(requisitionData.requisition_qty || requisitionData.procurement_qty);
        const units = deserializeArray(requisitionData.requisition_units || requisitionData.selected?.quotation_units);
        const amounts = deserializeArray(requisitionData.requisition_amounts || requisitionData.selected?.quotation_amounts);
        const discounts = deserializeArray(requisitionData.requisition_discounts || []);
        const vats = (requisitionData.requisition_vats || requisitionData.selected?.quotation_vats || '').split(',').map(v => parseFloat(v) || 0);
        
        setFormData({
            quotation_items: items,
            quotation_qty: qty,
            quotation_units: units,
            quotation_amounts: amounts,
            quotation_discounts: discounts.length ? discounts : items.map(() => 0),
            quotation_vats: vats.length ? vats : items.map(() => 0),
            quotation_before_discount: parseFloat(requisitionData.requisition_before_discount || 0),
            quotation_discount: parseFloat(requisitionData.requisition_discount || 0),
            quotation_subtotal: parseFloat(requisitionData.requisition_subtotal || 0),
            quotation_vat: parseFloat(requisitionData.requisition_vat || 0),
            quotation_total: parseFloat(requisitionData.requisition_total || requisitionData.selected?.quotation_total || 0),
            discount_type: requisitionData.requisition_discount_type || 'total',
            vat_type: requisitionData.requisition_vat_type || 'total'
        });
    };

    // Helper to deserialize PHP arrays
    const deserializeArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (typeof data === 'string') {
            try {
                // Try to parse as JSON first
                return JSON.parse(data);
            } catch {
                // If not JSON, might be serialized PHP array
                return [];
            }
        }
        return [];
    };

    // Load data on mount
    useEffect(() => {
        if (requisitionId) {
            loadPOData();
        }
    }, [requisitionId]);

    // Handle form input changes
    const handleInputChange = (field, index, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            
            if (index !== undefined) {
                newData[field] = [...prev[field]];
                newData[field][index] = value;
            } else {
                newData[field] = value;
            }
            
            return newData;
        });
    };

    // Calculate totals
    const calculateTotals = () => {
        let beforeDiscount = 0;
        let totalDiscount = 0;
        let lineVats = [];
        
        // Calculate line totals
        for (let i = 0; i < formData.quotation_items.length; i++) {
            const unit = parseFloat(formData.quotation_units[i] || 0);
            const qty = parseFloat(formData.quotation_qty[i] || 0);
            const lineDiscount = parseFloat(formData.quotation_discounts[i] || 0);
            
            const lineTotal = (unit * qty) - lineDiscount;
            beforeDiscount += (unit * qty);
            totalDiscount += lineDiscount;
            
            // Calculate VAT for this line
            const lineVat = formData.vat_type === 'line' && formData.quotation_vats[i] > 0 
                ? lineTotal * vatRate / 100 
                : 0;
            lineVats.push(lineVat);
            
            // Update line amount
            handleInputChange('quotation_amounts', i, lineTotal.toFixed(2));
        }
        
        // Apply discount strategy
        const discount = formData.discount_type === 'line' 
            ? totalDiscount 
            : parseFloat(formData.quotation_discount || 0);
            
        const subtotal = beforeDiscount - discount;
        
        // Calculate VAT
        const totalLineVat = lineVats.reduce((sum, vat) => sum + vat, 0);
        const vat = formData.vat_type === 'line' 
            ? totalLineVat 
            : subtotal * vatRate / 100;
            
        const total = subtotal + vat;
        
        // Update form data
        setFormData(prev => ({
            ...prev,
            quotation_before_discount: beforeDiscount.toFixed(2),
            quotation_discount: discount.toFixed(2),
            quotation_subtotal: subtotal.toFixed(2),
            quotation_vat: vat.toFixed(2),
            quotation_total: total.toFixed(2),
            quotation_vats: lineVats.map(v => v.toFixed(2))
        }));
    };

    // Recalculate when relevant fields change
    useEffect(() => {
        if (formData.quotation_items.length > 0) {
            calculateTotals();
        }
    }, [
        formData.quotation_units,
        formData.quotation_qty,
        formData.quotation_discounts,
        formData.quotation_discount,
        formData.discount_type,
        formData.vat_type
    ]);

    // Handle discount type change
    const handleDiscountTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            discount_type: type,
            quotation_discounts: type === 'line' 
                ? prev.quotation_discounts 
                : prev.quotation_items.map(() => 0),
            quotation_discount: type === 'total' ? prev.quotation_discount : 0
        }));
    };

    // Handle VAT type change
    const handleVatTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            vat_type: type,
            quotation_vats: type === 'line' 
                ? prev.quotation_items.map((_, i) => {
                    const unit = parseFloat(prev.quotation_units[i] || 0);
                    const qty = parseFloat(prev.quotation_qty[i] || 0);
                    const lineDiscount = parseFloat(prev.quotation_discounts[i] || 0);
                    const lineTotal = (unit * qty) - lineDiscount;
                    return (lineTotal * vatRate / 100).toFixed(2);
                })
                : prev.quotation_items.map(() => 0)
        }));
    };

    // Handle VAT checkbox change
    const handleVatCheckboxChange = (index, checked) => {
        if (formData.vat_type === 'line') {
            const newVats = [...formData.quotation_vats];
            if (checked) {
                const unit = parseFloat(formData.quotation_units[index] || 0);
                const qty = parseFloat(formData.quotation_qty[index] || 0);
                const lineDiscount = parseFloat(formData.quotation_discounts[index] || 0);
                const lineTotal = (unit * qty) - lineDiscount;
                newVats[index] = (lineTotal * vatRate / 100).toFixed(2);
            } else {
                newVats[index] = 0;
            }
            setFormData(prev => ({ ...prev, quotation_vats: newVats }));
        }
    };

    // Submit PO updates
    const handleSubmit = async () => {
        // Validate that new total doesn't exceed original
        const newTotal = parseFloat(formData.quotation_total);
        if (newTotal > originalTotal.current) {
            toast.error(`PO total cannot exceed original quote amount of R${originalTotal.current.toLocaleString()}`);
            return;
        }
        
        setSaving(true);
        try {
            const updateData = {
                requisition_id: requisitionId,
                ...formData
            };
            
            const response = await updatePurchaseOrder(updateData);
            
            if (response.success) {
                toast.success('Purchase order updated successfully');
                loadPOData(); // Refresh data
            } else {
                throw new Error(response.message || 'Failed to update purchase order');
            }
        } catch (error) {
            console.error('Error updating PO:', error);
            toast.error('Failed to update purchase order: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp * 1000).toLocaleDateString('en-ZA');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue"></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Purchase order not found</h3>
                <p className="mt-1 text-sm text-gray-500">
                    The requested purchase order could not be found.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate('/requisitions/po')}
                            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back to Purchase Orders
                        </button>
                    </div>
                </div>
                
                <div className="mt-4">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <Package className="h-8 w-8 text-custom-blue" />
                        Purchase Order: 1000{data.requisition_id}
                    </h1>
                </div>
            </div>

            {/* PO Details Card */}
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Assignment Info */}
                        {data.requisition_assign_id && (
                            <div className="border rounded-lg p-4">
                                <div className="text-sm font-medium text-gray-500">Assigned to:</div>
                                <div className="text-lg font-semibold text-gray-900">{data.assigned}</div>
                            </div>
                        )}
                        
                        {/* Requisition Number */}
                        <div className="border rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-500">Requisition Number:</div>
                            <div className="text-lg font-semibold text-custom-blue">EPR-{data.requisition_number}</div>
                        </div>
                        
                        {/* GL Code */}
                        <div className="border rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-500">GL Code:</div>
                            <div className="text-lg font-semibold text-gray-900">{data.gl_code}</div>
                        </div>
                    </div>
                    
                    {/* Project Description */}
                    <div className="mt-6 border rounded-lg p-4">
                        <div className="text-sm font-medium text-gray-500 mb-2">Project Description:</div>
                        <div className="text-lg text-gray-900">
                            {data.specification_title}
                            {data.requisition_capex && (
                                <span className="text-blue-600 ml-2">- Capex: {data.requisition_capex}</span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Additional Info */}
                <div className="px-6 py-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Requester Info */}
                        <div className="border rounded-lg p-4 bg-white">
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-500">Requester:</span>
                                    <span className="text-sm text-gray-900">{data.user_full_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm font-medium text-gray-500">Department:</span>
                                    <span className="text-sm text-gray-900">{data.department_name}</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Supplier Info */}
                        {data.selected && (
                            <div className="border rounded-lg p-4 bg-white">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium text-gray-500">Supplier:</span>
                                        <span className="text-sm text-gray-900">{data.selected.quotation_company_name}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-500">Quotation:</span>
                                        <button
                                            onClick={() => setShowQuoteModal(true)}
                                            className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                        >
                                            View
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* PO Items Form */}
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Requisition Items</h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-30">
                                        Item
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                        Quantity
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-15">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-15">
                                        Discount
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100 w-10">
                                        Options
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {formData.quotation_items.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => handleInputChange('quotation_items', index, e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-custom-blue focus:border-transparent"
                                            />
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <input
                                                type="number"
                                                value={formData.quotation_qty[index] || ''}
                                                onChange={(e) => handleInputChange('quotation_qty', index, e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-center focus:ring-2 focus:ring-custom-blue focus:border-transparent"
                                                required
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex">
                                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                    R
                                                </span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.quotation_units[index] || ''}
                                                    onChange={(e) => handleInputChange('quotation_units', index, e.target.value)}
                                                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-custom-blue focus:border-transparent"
                                                    required
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex">
                                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                    R
                                                </span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.quotation_discounts[index] || ''}
                                                    onChange={(e) => handleInputChange('quotation_discounts', index, e.target.value)}
                                                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-custom-blue focus:border-transparent"
                                                    readOnly={formData.discount_type === 'total'}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center bg-gray-100">
                                            <label className="flex items-center justify-center text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={parseFloat(formData.quotation_vats[index] || 0) > 0}
                                                    onChange={(e) => handleVatCheckboxChange(index, e.target.checked)}
                                                    disabled={formData.vat_type === 'total'}
                                                    className="mr-2"
                                                />
                                                Add VAT
                                            </label>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex">
                                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                    R
                                                </span>
                                                <input
                                                    type="text"
                                                    value={formatCurrency(formData.quotation_amounts[index] || 0)}
                                                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 bg-gray-50"
                                                    readOnly
                                                />
                                            </div>
                                            {parseFloat(formData.quotation_vats[index] || 0) > 0 && (
                                                <div className="mt-1">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        VAT: R{formatCurrency(formData.quotation_vats[index])}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                
                                {/* Totals Section */}
                                <tr className="bg-gray-50">
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-center bg-gray-100">
                                        <strong>Net</strong>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                R
                                            </span>
                                            <input
                                                type="text"
                                                value={formatCurrency(formData.quotation_before_discount)}
                                                className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 bg-gray-50"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* Discount Row */}
                                <tr className="bg-green-50">
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-right bg-green-100">
                                        <div className="space-y-2">
                                            <label className="flex items-center justify-end text-sm text-white">
                                                <input
                                                    type="radio"
                                                    name="discount_type"
                                                    value="line"
                                                    checked={formData.discount_type === 'line'}
                                                    onChange={() => handleDiscountTypeChange('line')}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700">Per Line</span>
                                            </label>
                                            <label className="flex items-center justify-end text-sm text-white">
                                                <input
                                                    type="radio"
                                                    name="discount_type"
                                                    value="total"
                                                    checked={formData.discount_type === 'total'}
                                                    onChange={() => handleDiscountTypeChange('total')}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700">Total</span>
                                            </label>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center bg-green-100">
                                        <strong className="text-green-800">Discount</strong>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                R
                                            </span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.quotation_discount}
                                                onChange={(e) => handleInputChange('quotation_discount', undefined, e.target.value)}
                                                className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-custom-blue focus:border-transparent"
                                                readOnly={formData.discount_type === 'line'}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* Subtotal Row */}
                                <tr className="bg-gray-50">
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-center bg-gray-100">
                                        <strong>Subtotal</strong>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                R
                                            </span>
                                            <input
                                                type="text"
                                                value={formatCurrency(formData.quotation_subtotal)}
                                                className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 bg-gray-50"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* VAT Row */}
                                <tr className="bg-blue-50">
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-right bg-blue-100">
                                        <div className="space-y-2">
                                            <label className="flex items-center justify-end text-sm">
                                                <input
                                                    type="radio"
                                                    name="vat_type"
                                                    value="line"
                                                    checked={formData.vat_type === 'line'}
                                                    onChange={() => handleVatTypeChange('line')}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700">Per Line</span>
                                            </label>
                                            <label className="flex items-center justify-end text-sm">
                                                <input
                                                    type="radio"
                                                    name="vat_type"
                                                    value="total"
                                                    checked={formData.vat_type === 'total'}
                                                    onChange={() => handleVatTypeChange('total')}
                                                    className="mr-2"
                                                />
                                                <span className="text-gray-700">Total</span>
                                            </label>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center bg-blue-100">
                                        <strong className="text-blue-800">VAT</strong>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                R
                                            </span>
                                            <input
                                                type="text"
                                                value={formatCurrency(formData.quotation_vat)}
                                                className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 bg-gray-50"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* Total Row */}
                                <tr className="bg-gray-100 font-bold">
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4"></td>
                                    <td className="px-6 py-4 text-center bg-gray-200">
                                        <strong>Total</strong>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                R
                                            </span>
                                            <input
                                                type="text"
                                                value={formatCurrency(formData.quotation_total)}
                                                className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 bg-gray-50 font-bold"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center px-6 py-3 bg-green-600 border border-transparent rounded-md shadow-sm text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Submit Purchase Order
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {/* Authorization and Request Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Request Details</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500">Requested by:</span>
                            <span className="text-sm text-gray-900">{data.user_full_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500">Requested at:</span>
                            <span className="text-sm text-gray-900">{formatDate(data.requisition_date)}</span>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Authorization Details</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500">Authorized by:</span>
                            <span className="text-sm text-gray-900">
                                {data.app_requisition?.user_full_name || 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-500">Authorized at:</span>
                            <span className="text-sm text-gray-900">
                                {data.selected?.quotation_time ? formatDate(data.selected.quotation_time) : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Motivation and Declaration */}
            {(data.requisition_motivation || data.requisition_declaration) && (
                <div className="mt-6 space-y-4">
                    {data.requisition_motivation && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Motivation for fewer quotes:</h3>
                            <p className="text-sm text-gray-700">{data.requisition_motivation}</p>
                        </div>
                    )}
                    
                    {data.requisition_declaration && (
                        <div className="bg-white shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">Declaration:</h3>
                            <p className="text-sm text-gray-700">{data.requisition_declaration}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Comments Section */}
            {data.comments && data.comments.length > 0 && (
                <div className="mt-6 bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Feedback</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Department
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Comment
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data.comments.map((comment, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {comment.department_name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {comment.user_full_name}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {comment.comment_text}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {new Date(comment.comment_date * 1000).toLocaleString('en-ZA')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewPurchaseOrder;
